import { Show, createMemo, createSignal, For, batch, onCleanup } from "solid-js";
import { useCalendar } from "../calendar/store";
import { orphanVoyages } from "../calendar/orphan-overrides";
import { viewTemplates } from "../calendar/view-templates";
import { useVessels } from "../vessels/store";
import { useVoyageDetails } from "../voyage-details/store";
import { useHashState } from "../../shared/routing/router";
import { connectionLastChecked, connectionStatus } from "./connection";
import { TextTooltip } from "../../shared/inputs/TextTooltip";
import { isCompact } from "../../shared/responsive/compact";
import s from "./StatusBar.module.css";

// Persistent footer status row. Shows the active tab's last status text +
// tone, plus a small summary of what's currently loaded so an empty status
// still tells the user something useful (e.g. "8 ports • 0 voyages" rather
// than a blank bar). Replaces the in-card `.status` divs that used to sit
// above the voyage details panel.
export function StatusBar() {
  const cal = useCalendar();
  const v = useVessels();
  const details = useVoyageDetails();
  const hash = useHashState();

  const active = () => hash().view;

  // Voyages currently loaded on the calendar whose overrides don't
  // resolve against the itinerary. Re-fires as details/overrides land
  // (overridesLoadedCount / cachedDetailsCount) — dependencies read
  // inside orphanVoyages via getCachedDetails/getOverrides don't
  // themselves trigger reactivity.
  const [orphanPopoverOpen, setOrphanPopoverOpen] = createSignal(false);
  let orphanPillEl: HTMLButtonElement | undefined;
  let orphanPopoverEl: HTMLDivElement | undefined;

  function closeOrphanPopover() {
    setOrphanPopoverOpen(false);
    orphanPillEl?.blur();
  }
  // Outside-click + Escape dismissal, matching the house convention
  // (UpstreamMenu / usePopupAnchor) — capture phase + pointerdown AND
  // mousedown since vis-timeline's Hammer.js can swallow bubble-phase
  // mousedown on anything overlapping the timeline.
  function onOrphanDocClick(e: MouseEvent) {
    if (!orphanPopoverOpen()) return;
    const t = e.target as Node;
    if (orphanPillEl?.contains(t)) return;
    if (orphanPopoverEl?.contains(t)) return;
    closeOrphanPopover();
  }
  function onOrphanKey(e: KeyboardEvent) {
    if (e.key === "Escape" && orphanPopoverOpen()) closeOrphanPopover();
  }
  document.addEventListener("pointerdown", onOrphanDocClick, true);
  document.addEventListener("mousedown", onOrphanDocClick, true);
  document.addEventListener("keydown", onOrphanKey);
  onCleanup(() => {
    document.removeEventListener("pointerdown", onOrphanDocClick, true);
    document.removeEventListener("mousedown", onOrphanDocClick, true);
    document.removeEventListener("keydown", onOrphanKey);
  });

  const orphanList = createMemo(() => {
    details.overridesLoadedCount();
    details.cachedDetailsCount();
    return orphanVoyages(Object.values(cal.state.voyages), details.getCachedDetails, details.getOverrides);
  });
  const orphanTotal = () => orphanList().reduce((n, v) => n + v.count, 0);

  function onGoToVoyage(vesselCode: string, voyageNo: string) {
    const voyage = Object.values(cal.state.voyages).find(
      (v) => v.vesselCode === vesselCode && v.voyageNo === voyageNo,
    );
    if (!voyage) return;
    batch(() => {
      details.setSelection("calendar", voyage);
      cal.setVesselFilter(voyage.vesselCode);
    });
    void details.warmDetails(voyage.vesselCode, voyage.voyageNo);
    details.setPopupOpen(true);
    closeOrphanPopover();
  }

  const message = createMemo(() => {
    const view = active();
    // Settings has no live status — only an idle summary. Anything from
    // the calendar / vessels status fields would be misleading here
    // (we're not loading voyages from this tab).
    const st = view === "vessels"
      ? v.state.status
      : view === "calendar"
        ? cal.state.status
        : { text: "", tone: "info" as const };

    // Live status (from store.status) takes the whole text and tone.
    if (st.text) return { text: st.text, tone: st.tone };

    // Idle summary — pipe-delimited "Loaded:" line on calendar / vessels
    // so the counts read uniformly across both tabs. Settings keeps its
    // own bullet-style summary (different domain entirely).
    // In compact mode (driven by the same data-compact trigger as the
    // tabs / brand collapse) the long breakdown is replaced with the
    // sum of loaded objects so the status bar fits a phone width
    // without truncating.
    const detailsCount = details.cachedDetailsCount();
    const isLoading = details.isLoadingDetails();
    const compact = isCompact();
    if (view === "vessels") {
      const total = v.state.voyages.length;
      const shown = v.filtered().length;
      if (total === 0) return { text: "Ready", tone: "info" as const };
      if (compact) {
        const sum = total + detailsCount;
        return { text: `Loaded: ${sum} object${sum === 1 ? "" : "s"}${isLoading ? " (loading)" : ""}`, tone: isLoading ? "loading" as const : "info" as const };
      }
      const voyagesPart = total === shown ? `${total} voyages` : `${shown} of ${total} voyages`;
      const text = `Loaded: ${voyagesPart} | ${detailsCount} voyage details${isLoading ? " (loading)" : ""}`;
      return { text, tone: isLoading ? "loading" as const : "info" as const };
    }
    if (view === "settings") {
      const coloured = Object.keys(cal.state.portColors).length;
      const events = cal.state.dateEvents.length;
      const templates = viewTemplates().length;
      if (compact) {
        const sum = coloured + events + templates;
        return { text: `Loaded: ${sum} object${sum === 1 ? "" : "s"}`, tone: "info" as const };
      }
      return {
        text: `${coloured} coloured terminal${coloured === 1 ? "" : "s"} • ${events} date event${events === 1 ? "" : "s"} • ${templates} view template${templates === 1 ? "" : "s"}`,
        tone: "info" as const,
      };
    }
    // Calendar idle summary.
    const ports = cal.state.selectedPorts.length;
    const voyages = Object.keys(cal.state.voyages).length;
    if (ports === 0) return { text: "Select ports to begin", tone: "info" as const };
    if (compact) {
      const sum = ports + voyages + detailsCount;
      return { text: `Loaded: ${sum} object${sum === 1 ? "" : "s"}${isLoading ? " (loading)" : ""}`, tone: isLoading ? "loading" as const : "info" as const };
    }
    const text = `Loaded: ${ports} ports | ${voyages} voyages | ${detailsCount} voyage details${isLoading ? " (loading)" : ""}`;
    return { text, tone: isLoading ? "loading" as const : "info" as const };
  });

  const connTitle = () => {
    const checked = connectionLastChecked();
    const stamp = checked
      ? checked.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      : "—";
    switch (connectionStatus()) {
      case "connected":    return `Backend reachable (last check ${stamp})`;
      case "disconnected": return `Backend unreachable (last check ${stamp})`;
      case "checking":     return "Checking backend connection…";
    }
  };

  return (
    <footer
      class={`${s.bar} ${message().tone === "error" ? s.error : ""} ${message().tone === "loading" ? s.loading : ""}`}
      role="status"
      aria-live="polite"
    >
      <Show when={message().tone === "loading"}>
        <span class={s.spinner} aria-hidden="true" />
      </Show>
      <span class={s.text}>{message().text}</span>
      <Show when={orphanList().length > 0}>
        <button
          type="button"
          ref={(el) => (orphanPillEl = el)}
          class={s.orphanPill}
          onClick={() => setOrphanPopoverOpen((o) => !o)}
        >
          ⚠ {orphanTotal()} override{orphanTotal() === 1 ? "" : "s"} on {orphanList().length} voyage{orphanList().length === 1 ? "" : "s"} need fixing
        </button>
        <Show when={orphanPopoverOpen()}>
          <div ref={(el) => (orphanPopoverEl = el)} class={s.orphanPopover}>
            <For each={orphanList()}>
              {(voyage) => (
                <div class={s.orphanPopoverRow}>
                  <span>{voyage.vesselCode} · {voyage.voyageReference}</span>
                  <span class="muted">{voyage.count} override{voyage.count === 1 ? "" : "s"}</span>
                  <button type="button" class={s.orphanGo} onClick={() => onGoToVoyage(voyage.vesselCode, voyage.voyageNo)}>Open</button>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
      <TextTooltip
        text="Connection"
        description={connTitle()}
        stripe={
          connectionStatus() === "connected" ? "#10b981" :
          connectionStatus() === "disconnected" ? "#ef4444" :
          "#f59e0b"
        }
        mobileTap
      >
      <span class={s.conn} aria-label={connTitle()}>
        <span class={s.connText}>
          {connectionStatus() === "connected"   ? "Connected" :
           connectionStatus() === "disconnected" ? "Disconnected" :
           "Checking…"}
        </span>
        <span
          class={`${s.connDot} ${
            connectionStatus() === "connected" ? s.connOk :
            connectionStatus() === "disconnected" ? s.connDown :
            s.connChecking
          }`}
          aria-hidden="true"
        />
      </span>
      </TextTooltip>
    </footer>
  );
}
